import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

import mainLogo from '../assets/images/new/logoCC.png';

const Footer = () => {
    const { language } = useLanguage();

    // Bilingual content
    const content = {
        en: {
            tagline: "Creating exceptional digital experiences that drive growth and captivate audiences.",
            services: "Services",
            contact: "Contact",
            rights: "© {year} Creativad. All rights reserved.",
            privacy: "Privacy Policy",
            terms: "Terms of Service",
            cookies: "Cookies",
            servicesList: ['Corporate Branding', 'Advertising Campaigns', 'Production'],
            email: "info@creativad.co"
        },
        ar: {
            tagline: "نخلق تجارب رقمية استثنائية تحقق النمو وتجذب الجمهور.",
            services: "الخدمات",
            contact: "اتصل بنا",
            rights: "© {year} كريتيفاد. جميع الحقوق محفوظة.",
            privacy: "سياسة الخصوصية",
            terms: "شروط الخدمة",
            cookies: "الكوكيز",
            servicesList: ['الإنتاج', 'الحملات الإعلانية', 'العلامات التجارية للشركات'],
            email: "info@creativad.co"
        }
    }[language];

    // Helper object for language-dependent CSS classes
    const langStyles = {
        dir: language === 'ar' ? 'rtl' : 'ltr',
        textAlign: language === 'ar' ? 'text-right' : 'text-left',
        marginEndLogo: language === 'ar' ? 'ml-4' : 'mr-4',
        marginEndIcon: language === 'ar' ? 'ml-3' : 'mr-3',
        socialIconSpacing: language === 'ar' ? 'space-x-reverse space-x-4' : 'space-x-4'
    };

    return (
        <footer
            id="footer"
            className="py-20 relative bg-light"
        >
            {/* Decorative elements */}
            <div className="absolute bottom-20 left-1/4 w-64 h-64 bg-[#1E372B]/10 rounded-full blur-[100px]" />
            <div className="absolute top-1/3 right-1/4 w-48 h-48 bg-[#1E372B]/10 rounded-full blur-[100px]" />

            <div className={`max-w-screen-2xl mx-auto px-6 md:pl-72 md:pr-12 relative z-10`}>
                <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12`} dir={langStyles.dir}>
                    {/* Brand Column */}
                    <div className="md:col-span-2">
                        <div className="flex items-start mb-6">
                            <img
                                src={mainLogo}
                                alt="Logo"
                                className={`h-14 w-auto flex-shrink-0 ${langStyles.marginEndLogo}`}
                            />
                            <div className={langStyles.textAlign}>
                                <motion.h2
                                    className="text-2xl font-greycliff font-bold text-[#1E372B]"
                                    whileHover={{ scale: 1.02 }}
                                >
                                    Creativad<span className="text-[#50A795]">.</span>
                                </motion.h2>
                                <p className="text-[#1E372B] mt-2 max-w-md">
                                    {content.tagline}
                                </p>
                            </div>
                        </div>

                        <div className={`flex mt-6 ${langStyles.socialIconSpacing}`}>
                            {/* Social Icons */}
                            <motion.a href="info@creativad.co" className="w-10 h-10 rounded-lg flex items-center justify-center text-[#1E372B] hover:text-[#50A795] transition-colors" whileHover={{ y: -3 }}><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M7.635 10.909v2.619h4.335c-.173 1.125-1.31 3.295-4.331 3.295-2.604 0-4.731-2.16-4.731-4.823 0-2.662 2.122-4.822 4.728-4.822 1.485 0 2.479.633 3.045 1.178l2.073-1.994c-1.33-1.245-3.056-1.995-5.115-1.995C3.412 4.365 0 7.785 0 12s3.414 7.635 7.635 7.635c4.41 0 7.332-3.098 7.332-7.461 0-.501-.054-.885-.12-1.265H7.635zm16.365 0h-2.183V8.726h-2.183v2.183h-2.182v2.181h2.184v2.184h2.189v-2.184H24"/></svg></motion.a>
                            <motion.a href="https://www.instagram.com/creativad.lb/" className="w-10 h-10 rounded-lg flex items-center justify-center text-[#1E372B] hover:text-[#50A795] transition-colors" whileHover={{ y: -3 }}><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg></motion.a>
                            <motion.a href="https://www.facebook.com/share/1AfwXvQU9y/" className="w-10 h-10 rounded-lg flex items-center justify-center text-[#1E372B] hover:text-[#50A795] transition-colors" whileHover={{ y: -3 }}><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/></svg></motion.a>
                            <motion.a href="https://www.linkedin.com/company/creativad-agency/" className="w-10 h-10 rounded-lg flex items-center justify-center text-[#1E372B] hover:text-[#50A795] transition-colors" whileHover={{ y: -3 }}><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z"/></svg></motion.a>
                            <motion.a href="https://www.behance.net/creativad" className="w-10 h-10 rounded-lg flex items-center justify-center text-[#1E372B] hover:text-[#50A795] transition-colors" whileHover={{ y: -3 }}><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M22 7h-7v-2h7v2zm1.726 10c-.442 1.297-2.029 3-5.101 3-3.074 0-5.564-1.729-5.564-5.675 0-3.91 2.325-5.92 5.466-5.92 3.082 0 4.964 1.782 5.375 4.426.078.506.109 1.188.095 2.14h-8.027c.13 3.211 3.483 3.312 4.588 2.029h3.168zm-7.686-4h4.965c-.105-1.547-1.136-2.219-2.477-2.219-1.466 0-2.277.768-2.488 2.219zm-9.574 6.988h-6.466v-14.967h6.953c5.476.081 5.58 5.444 2.72 6.906 3.461 1.26 3.577 8.061-3.207 8.061zm-3.466-8.988h3.584c2.508 0 2.906-3-.312-3h-3.272v3zm3.391 3h-3.391v3.016h3.341c3.055 0 2.868-3.016.05-3.016z"/></svg></motion.a>
                        </div>
                    </div>

                    {/* Services Column */}
                    <div className={langStyles.textAlign}>
                        <h3 className="text-lg font-greycliff font-bold mb-4 text-[#1E372B]">{content.services}</h3>
                        <ul className="space-y-3">
                            {content.servicesList.map(s => <li key={s}><a href="#projects" className="text-[#1E372B] hover:text-[#50A795] transition-colors block py-1">{s}</a></li>)}
                        </ul>
                    </div>

                    {/* === FINAL CONTACT COLUMN LAYOUT === */}
                    <div className={`${langStyles.textAlign} lg:col-span-2 md:col-span-2`}>
                        <h3 className="text-lg font-greycliff font-bold mb-4 text-[#1E372B]">{content.contact}</h3>

                        {/* Main 2-column grid for the contact section */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 text-[#1E372B] text-sm">

                            {/* Column 1: Email and Lebanon */}
                            <div className="space-y-6">
                                {/* Email */}
                                <div className="flex items-start">
                                    <svg className={`w-5 h-5 mt-1 text-[#50A795] flex-shrink-0 ${langStyles.marginEndIcon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    <a href={`mailto:${content.email}`} className="hover:underline ms-2">{content.email}</a>
                                </div>

                                {/* Lebanon */}
                                <div>
                                    <p className="font-semibold">Lebanon</p>
                                    <ul className="mt-2 space-y-1">
                                        <li><a href="tel:0096181886707" className="hover:underline">Hoteit: 00961 81886707</a></li>
                                        <li><a href="tel:009613273943" className="hover:underline">Office: 00961 3273943</a></li>
                                    </ul>
                                </div>
                            </div>

                            {/* Column 2: USA and Qatar */}
                            <div className="space-y-6">
                                {/* USA */}
                                <div>
                                    <p className="font-semibold">USA</p>
                                    <ul className="mt-2 space-y-1">
                                        <li><a href="tel:+13132343435" className="hover:underline">Hoteit: +1 (313) 234-3435</a></li>
                                        <li><a href="tel:+17348990700" className="hover:underline">Office: +1 (734) 899-0700</a></li>
                                    </ul>
                                </div>

                                {/* Qatar */}
                                <div>
                                    <p className="font-semibold">Qatar</p>
                                    <ul className="mt-2 space-y-.5">
                                        <li><a href="tel:0097455787652" className="hover:underline">Hoteit: 00 974 5578 7652</a></li>
                                        <li><a href="tel:0097455996662" className="hover:underline">Office: 00 974 5599 6662</a></li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Divider and Copyright */}
                <div className="border-t border-[#1E372B]/20 my-12"></div>
                <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
                    <p className="text-[#1E372B]/70 mb-4 md:mb-0">
                        {content.rights.replace('{year}', new Date().getFullYear())}
                    </p>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center">
                        <a href="#" className="text-[#1E372B]/70 hover:text-[#50A795] transition-colors text-sm">{content.privacy}</a>
                        <a href="#" className="text-[#1E372B]/70 hover:text-[#50A795] transition-colors text-sm">{content.terms}</a>
                        <a href="#" className="text-[#1E372B]/70 hover:text-[#50A795] transition-colors text-sm">{content.cookies}</a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;